import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onViewDetails }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getTechBadgeColor = (tech) => {
    const colors = {
      'React': 'bg-blue-100 text-blue-800',
      'Node.js': 'bg-green-100 text-green-800',
      'MongoDB': 'bg-green-100 text-green-800',
      'Express': 'bg-gray-100 text-gray-800',
      'TypeScript': 'bg-blue-100 text-blue-800',
      'Next.js': 'bg-black text-white',
      'Tailwind': 'bg-cyan-100 text-cyan-800',
      'Firebase': 'bg-orange-100 text-orange-800',
      'PostgreSQL': 'bg-blue-100 text-blue-800',
      'Python': 'bg-yellow-100 text-yellow-800',
      'Django': 'bg-green-100 text-green-800',
      'Vue.js': 'bg-green-100 text-green-800',
      'AWS': 'bg-orange-100 text-orange-800',
      'Docker': 'bg-blue-100 text-blue-800',
      'GraphQL': 'bg-pink-100 text-pink-800'
    };
    return colors?.[tech] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div 
      className="bg-card rounded-xl shadow-subtle hover:shadow-moderate transition-all duration-300 overflow-hidden group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Project Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={project?.image}
          alt={project?.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className={`absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="flex space-x-3">
            {project?.liveUrl && (
              <Button
                variant="default"
                size="sm"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={(e) => {
                  e?.stopPropagation();
                  window.open(project?.liveUrl, '_blank');
                }}
              >
                Live Demo
              </Button>
            )}
            {project?.githubUrl && (
              <Button
                variant="outline"
                size="sm"
                iconName="Github"
                iconPosition="left"
                onClick={(e) => {
                  e?.stopPropagation();
                  window.open(project?.githubUrl, '_blank');
                }}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                Code
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Project Content */}
      <div className="p-6">
        {/* Project Type Badge */}
        <div className="flex items-center justify-between mb-3">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            project?.type === 'Web App' ? 'bg-blue-100 text-blue-800' :
            project?.type === 'Mobile App' ? 'bg-green-100 text-green-800' :
            project?.type === 'API'? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {project?.type}
          </span>
          <span className="text-sm text-muted-foreground">{project?.year}</span>
        </div>

        {/* Project Title */}
        <h3 className="text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors duration-200">
          {project?.title}
        </h3>

        {/* Project Description */}
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {project?.description}
        </p>

        {/* Technology Stack */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project?.technologies?.slice(0, 4)?.map((tech, index) => (
            <span
              key={index}
              className={`px-2 py-1 text-xs font-medium rounded-md ${getTechBadgeColor(tech)}`}
            >
              {tech}
            </span>
          ))}
          {project?.technologies?.length > 4 && (
            <span className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-600">
              +{project?.technologies?.length - 4} more
            </span>
          )}
        </div>

        {/* Project Stats */}
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={16} />
              <span>{project?.duration}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Users" size={16} />
              <span>{project?.teamSize}</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Star" size={16} className="text-yellow-500" />
            <span>{project?.rating}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            variant="default"
            size="sm"
            fullWidth
            onClick={() => onViewDetails(project)}
            iconName="Eye"
            iconPosition="left"
          >
            View Details
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;